import { useState, useEffect } from 'react';
import Select, { components } from 'react-select';
import pluralize from 'pluralize';
import { createPortal } from 'react-dom';
import { IoArrowBackSharp } from 'react-icons/io5';
import { ActionPanel } from '../../General/ActionPanel';
import Loader from '../../General/Loader';
import { NewBrandModal } from './components/NewBrandModal';
import { CategoryProductsStyleguide } from './components/CategoryProductsStyleguide';
import { useProductForm } from './hooks/useProductForm';
import { useBrandProductSimilarity } from './hooks/useBrandProductSimilarity';
import { useBrandDomainWarning } from './hooks/useBrandDomainWarning';
import { Images } from './components/Images';
import { getSmartImage } from '../../../helpers/helpers';
import './ProductForm.scss';

const MAX_BRAND_OPTIONS_DISPLAYED = 250;

/**
 * @typedef {Object} ImageObject
 * @property {number} id
 * @property {string} image
 * @property {boolean} isCover
 * @property {boolean} isFeatured
 * @property {string} style
 * @property {number} sortOrderRank
 */

/**
 * @typedef {Object} ProductSuggestions
 * @property {string} [brandName]
 * @property {string} [domain]
 * @property {number} [categoryId]
 * @property {string} [title]
 * @property {string[]} [images] - Array of image URLs
 */

/**
 * @typedef {Object} ProductFormProps
 * @property {number} [productId] - ID of existing product to edit
 * @property {ProductSuggestions} [suggestions] - Suggested values for new product creation
 * @property {function({product: Object, userCreatedBrand: boolean, userCreatedProduct: boolean}): void} [onSave] - Callback when product is saved
 * @property {number} [pinCount] - Number of pins to display
 * @property {function(): void} [onBackClick] - Callback for back button click
 * @property {string} [categoryStyleguideTarget] - DOM element ID for styleguide portal
 * @property {function(productId: number, product: Object): void} [onExistingProductSelected] - Callback when the user picks an existing product instead of saving a new one
 * @property {boolean} [showDescriptionFields] - Whether to show the description fields
 * @property {boolean} [showMetaOptions] - Whether to show the meta options such as hidden from rankings and pre-render for SEO
 * @property {string} [domain] - Domain to check for existing products when brand is selected
 * @property {boolean} [disabled] - Whether to disable all form inputs and buttons
 * @property {Array<{id: number, name: string, productCount: number}>} [brandSuggestions] - Catalog brands offered as one-click picks under the brand field
 * @property {boolean} [saveOnCommandEnter] - Whether ⌘↵ (Ctrl+Enter) anywhere on the page saves the form
 */

/**
 * @param {ProductFormProps} props
 */
export const ProductForm = ({
  productId,
  suggestions = {},
  onSave,
  pinCount,
  onBackClick,
  categoryStyleguideTarget,
  saveButtonLabel = 'Save',
  onExistingProductSelected,
  showDescriptionFields = true,
  showMetaOptions = true,
  domain,
  disabled = false,
  brandSuggestions = [],
  saveOnCommandEnter = false
}) => {
  const {
    selectedCategory,
    setSelectedCategory,
    title,
    setTitle,
    images,
    setImages,
    selectedBrand,
    showAddBrandModal,
    brandOptions,
    handleBrandChange,
    handleNewBrandSelect,
    handleConflictBrandSelect,
    openAddBrandModal,
    closeAddBrandModal,
    isSaving,
    isLoading,
    loadError,
    handleSave,
    duplicateProduct,
    categoryOptions,
    isHidden,
    setIsHidden,
    isSeoPrerendered,
    setIsSeoPrerendered,
    description,
    setDescription,
    descriptionWhyBuy,
    setDescriptionWhyBuy,
    descriptionWhatPeopleSay,
    setDescriptionWhatPeopleSay,
    isDescriptionManual,
    setIsDescriptionManual,
    isDescriptionWhyBuyManual,
    setIsDescriptionWhyBuyManual,
    isDescriptionWhatPeopleSayManual,
    setIsDescriptionWhatPeopleSayManual
  } = useProductForm(productId, suggestions);

  const [hasTitleBeenEdited, setHasTitleBeenEdited] = useState(false);

  const showDomainWarning = useBrandDomainWarning(selectedBrand, domain);

  const { similarProducts, error } = useBrandProductSimilarity({
    brandId: selectedBrand?.value,
    title,
    currentProductId: productId
  });
  const suggestedTitleAlreadyExists =
    !productId && similarProducts.some(product => product.title.trim().toLowerCase() === title.trim().toLowerCase());

  useEffect(() => {
    setHasTitleBeenEdited(false);
  }, [productId]);

  useEffect(() => {
    if (!saveOnCommandEnter) return undefined;
    const saveOnKeyDown = e => {
      if (!(e.metaKey || e.ctrlKey) || e.key !== 'Enter' || e.defaultPrevented || showAddBrandModal) return;
      e.preventDefault();
      if (!isLoading && !loadError && !isSaving && !disabled) handleSave(onSave);
    };
    window.addEventListener('keydown', saveOnKeyDown);
    return () => window.removeEventListener('keydown', saveOnKeyDown);
  });

  const unselectedBrandSuggestions = brandSuggestions.filter(brand => brand.id !== selectedBrand?.value);

  const header = (
    <>
      <div className='back'>
        {onBackClick && (
          <button onClick={onBackClick}>
            <IoArrowBackSharp size={20} />
            Back
          </button>
        )}
      </div>
      <div className='header-action'>
        {!isLoading && !loadError && (
          <button onClick={() => handleSave(onSave)} disabled={isSaving || disabled}>
            {/* eslint-disable-next-line no-extra-boolean-cast -- it's a number, so valid use of !!. Eslint being dumb. */}
            {isSaving ? 'Saving...' : saveButtonLabel} {!!pinCount ? `(${pinCount})` : ''}
          </button>
        )}
      </div>
    </>
  );

  if (isLoading || loadError) {
    return (
      <ActionPanel header={header}>
        <div className='product-form-loading'>{isLoading ? <Loader variant='circle' size={48} /> : <p>Error loading product: {loadError}</p>}</div>
      </ActionPanel>
    );
  }
  return (
    <>
      <ActionPanel header={header}>
        <div className='product-form'>
          <div className='product-categorization-panel'>
            <label>
              Brand
              <Select
                options={brandOptions}
                placeholder='Select a brand'
                value={selectedBrand}
                onChange={handleBrandChange}
                isDisabled={disabled}
                components={{
                  MenuList: props => {
                    const limited = props.children?.slice && props.children.slice(0, MAX_BRAND_OPTIONS_DISPLAYED);
                    const hasInputValue = Boolean(props.selectProps?.inputValue);
                    return (
                      <components.MenuList {...props}>
                        {hasInputValue ? (
                          limited ? (
                            limited
                          ) : (
                            <div style={{ padding: '8px', textAlign: 'center', fontStyle: 'italic' }}>
                              No results, please make a new brand on the AllBrands tab.
                            </div>
                          )
                        ) : (
                          <div style={{ padding: '8px', textAlign: 'center', fontStyle: 'italic' }}>Search for brands by name or domain</div>
                        )}
                        {hasInputValue && props.children.length > MAX_BRAND_OPTIONS_DISPLAYED && (
                          <div style={{ padding: '8px', textAlign: 'center', fontStyle: 'italic' }}>
                            Showing first {MAX_BRAND_OPTIONS_DISPLAYED} results
                          </div>
                        )}
                      </components.MenuList>
                    );
                  }
                }}
              />
              {unselectedBrandSuggestions.length > 0 && (
                <div className='brand-suggestions'>
                  {unselectedBrandSuggestions.map(brand => (
                    <button
                      key={brand.id}
                      type='button'
                      className='brand-suggestion-button'
                      onClick={() => handleBrandChange({ value: brand.id, label: brand.name })}
                      disabled={disabled}
                    >
                      Use {brand.name} · {pluralize('product', brand.productCount, true)}
                    </button>
                  ))}
                </div>
              )}
              <button className='add-brand-button' onClick={openAddBrandModal} disabled={disabled}>
                Add a new brand
              </button>
            </label>

            {showDomainWarning && (
              <div className='domain-warning'>
                ⚠️ This is the first time a link from <strong>{domain}</strong> has been set on{' '}
                <strong>{selectedBrand?.label ?? 'this brand'}</strong>. Are you sure you've selected the correct brand?
              </div>
            )}

            <label>
              Category
              <Select
                options={categoryOptions}
                placeholder='Select a category'
                value={selectedCategory}
                onChange={option => setSelectedCategory(option)}
                isDisabled={disabled}
              />
            </label>

            <label>
              Title
              <input
                type='text'
                value={title}
                onChange={e => {
                  setTitle(e.target.value);
                  setHasTitleBeenEdited(true);
                }}
                disabled={disabled}
              />
            </label>

            {showDescriptionFields && (
              <>
                <label>
                  <div className='label-title'>
                    Description <span className='manual-edit-indicator'>{isDescriptionManual ? 'Manually edited' : 'AI Generated'}</span>
                  </div>
                  <textarea
                    rows={4}
                    value={description}
                    onChange={e => {
                      setDescription(e.target.value);
                      setIsDescriptionManual(true);
                    }}
                    placeholder='Product description...'
                    disabled={disabled}
                  />
                </label>

                <label>
                  <div className='label-title'>
                    Why Buy <span className='manual-edit-indicator'>{isDescriptionWhyBuyManual ? 'Manually edited' : 'AI Generated'}</span>
                  </div>
                  <textarea
                    rows={3}
                    value={descriptionWhyBuy}
                    onChange={e => {
                      setDescriptionWhyBuy(e.target.value);
                      setIsDescriptionWhyBuyManual(true);
                    }}
                    placeholder='Why should customers buy this product?'
                    disabled={disabled}
                  />
                </label>

                <label>
                  <div className='label-title'>
                    What People Say{' '}
                    <span className='manual-edit-indicator'>{isDescriptionWhatPeopleSayManual ? 'Manually edited' : 'AI Generated'}</span>
                  </div>
                  <textarea
                    rows={3}
                    value={descriptionWhatPeopleSay}
                    onChange={e => {
                      setDescriptionWhatPeopleSay(e.target.value);
                      setIsDescriptionWhatPeopleSayManual(true);
                    }}
                    placeholder='Customer testimonials or reviews...'
                    disabled={disabled}
                  />
                </label>
              </>
            )}

            {duplicateProduct && (
              <div className='similar-products-warning duplicate'>
                <div className='warning-header'>⚠️ This product already exists for {selectedBrand?.label}:</div>
                <ul className='similar-products-list'>
                  <li>
                    {duplicateProduct.image && <img src={getSmartImage(duplicateProduct.image)} alt='' />}
                    <span className='product-title'>{duplicateProduct.title}</span>
                    {onExistingProductSelected && (
                      <button
                        className='select-button'
                        onClick={() => onExistingProductSelected(duplicateProduct.id, duplicateProduct)}
                        disabled={disabled}
                      >
                        Use this product
                      </button>
                    )}
                  </li>
                </ul>
              </div>
            )}

            {!duplicateProduct && (hasTitleBeenEdited || suggestedTitleAlreadyExists) && !error && similarProducts.length > 0 && (
              <div className='similar-products-warning'>
                <div className='warning-header'>⚠️ Similar products from {selectedBrand?.label}:</div>
                <ul className='similar-products-list'>
                  {similarProducts.slice(0, 5).map(product => (
                    <li key={product.id}>
                      <span className='product-title'>{product.title}</span>
                      {onExistingProductSelected && (
                        <button className='select-button' onClick={() => onExistingProductSelected(product.id, product)} disabled={disabled}>
                          Select
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {showMetaOptions && (
              <div className='settings-section'>
                <label className='checkbox-label'>
                  <input type='checkbox' checked={isHidden} onChange={e => setIsHidden(e.target.checked)} disabled={disabled} />
                  Hidden From Rankings
                </label>

                <label className='checkbox-label'>
                  <input type='checkbox' checked={isSeoPrerendered} onChange={e => setIsSeoPrerendered(e.target.checked)} disabled={disabled} />
                  Pre-render for SEO
                </label>
              </div>
            )}
          </div>

          <div className='images-panel'>
            <Images images={images} setImages={setImages} disabled={disabled} />
          </div>
        </div>
      </ActionPanel>

      <NewBrandModal
        open={showAddBrandModal}
        onRequestClose={closeAddBrandModal}
        onSelectBrand={handleNewBrandSelect}
        onConflictBrandSelect={handleConflictBrandSelect}
      />

      {selectedCategory &&
        (categoryStyleguideTarget ? (
          (() => {
            const target = document.getElementById(categoryStyleguideTarget);
            return target ? createPortal(<CategoryProductsStyleguide categoryId={selectedCategory.value} />, target) : null;
          })()
        ) : (
          <CategoryProductsStyleguide categoryId={selectedCategory.value} />
        ))}
    </>
  );
};
